import { useState } from "react";
import { CircularProgress } from "@mui/material";

import PrimaryButton from "../../../Components/Buttons/primary-button";

import { useModalContext } from "../../../Contexts/ModalContext";

import { useImagePreload, usePrograms } from "../../../CustomHooks/Queries";

import { modalVariants } from "../../../Utils/constants";

import { ArrowLeft, ArrowRight } from "../../../Assets/Icons";
import { Ellipse } from "../../../Assets/Images";

import "./colorado-renter-rewards.scss";

const coloradoRenterRewardsModalClassName = "colorado-renter-program showcase-feature full-modal-width hide-header";

const showModalHeader = (className) => className.replace("hide-header", "position-close-button-over-content");

export default function renderColoradoRenterRewards(modalContext, props = {}) {
  const { updateModalComponent, setModalVariant, setModalClassName, setModalAriaLabel } = modalContext;
  const { occupyFullscreen = false, skipProgramIntro = false } = props;

  updateModalComponent(() => <ColoradoRenterRewards {...props} />);
  setModalVariant(modalVariants.fullScreenDialog);
  setModalAriaLabel?.("Colorado Renter Rewards");
  setModalClassName(
    `${skipProgramIntro ? showModalHeader(coloradoRenterRewardsModalClassName) : coloradoRenterRewardsModalClassName}${
      occupyFullscreen ? " fullscreen-on-all" : ""
    }`
  );
}

const ColoradoRenterRewards = ({ skipProgramIntro = false }) => {
  const [showBenefits, setShowBenefits] = useState(skipProgramIntro);
  const [benefitIndex, setBenefitIndex] = useState(0);

  const { modalClassName, setModalClassName, onModalClose } = useModalContext();

  const { isLoading: isLoadingPrograms, data: programs } = usePrograms();

  const { isLoading: isLoadingImages, data: images } = useImagePreload(
    [
      programs?.my_program?.image_src?.primary,
      ...(!!programs?.my_program ? programs.my_program.details.map(({ image_src }) => image_src?.primary) : []),
    ],
    { enabled: !!programs?.my_program }
  );

  const handleLearnMore = () => {
    if (!showBenefits) {
      setShowBenefits(true);
      setModalClassName(showModalHeader(modalClassName));
    } else {
      setBenefitIndex((p) => p + 1);
    }
  };

  const isLoading = isLoadingPrograms || isLoadingImages;

  return isLoading ? (
    <div className="loading-content">
      <CircularProgress />
    </div>
  ) : (
    <>
      <div className={`modal-image-carousel ${showBenefits ? "with-ellipse" : "colorado-logo"}`}>
        <img src={images?.[benefitIndex + (showBenefits ? 1 : 0)]} alt="" />
        {showBenefits && <Ellipse className="ellipse" />}
        <div className="modal-content-ellipse"></div>
      </div>
      <div className="modal-dialog-content">
        {showBenefits && (
          <span className="benefit-badge mt-3">{programs?.my_program?.details?.[benefitIndex]?.badge}</span>
        )}
        <h3 className="benefit-title mt-3 mb-4">
          {showBenefits ? programs?.my_program?.details?.[benefitIndex]?.title : programs?.my_program?.title}
        </h3>
        <span className="benefit-description">
          {showBenefits
            ? programs?.my_program?.details?.[benefitIndex]?.description
            : programs?.my_program?.description}
        </span>
        <div className="benefit-actions mt-8 mb-6">
          {!showBenefits ? (
            <>
              <PrimaryButton onClick={handleLearnMore}>{programs?.my_program?.primary_action?.label}</PrimaryButton>
              <button className="learn-more-button" onClick={onModalClose}>
                {programs?.my_program?.secondary_action?.label}
              </button>
            </>
          ) : (
            <div className="benefit-control-container">
              <button
                className="benefit-control-prev"
                onClick={() => setBenefitIndex((p) => p - 1)}
                disabled={benefitIndex <= 0}
                aria-label="Previous benefit"
              >
                <ArrowLeft />
              </button>
              <div className="benefit-indicators" role="tablist" aria-label="Program benefits">
                {programs?.my_program?.details?.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`benefit-indicator${benefitIndex === idx ? " active" : ""}`}
                    onClick={() => setBenefitIndex(idx)}
                    aria-label={`Show benefit ${idx + 1} of ${programs?.my_program?.details?.length}`}
                    aria-current={benefitIndex === idx ? "true" : undefined}
                  />
                ))}
              </div>
              <button
                className="benefit-control-next"
                onClick={() => setBenefitIndex((p) => p + 1)}
                disabled={benefitIndex >= programs?.my_program?.details?.length - 1}
                aria-label="Next benefit"
              >
                <ArrowRight />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
